import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  Meeting,
  MeetingListParams,
  CreateMeetingData,
  UpdateMeetingData,
  CalendarEvent,
} from '../../types';
import meetingService from '../../services/meetingService';

// State interfaces
export interface MeetingFilters {
  search: string;
  status: string;
  visibility: string;
  categoryId: string;
  tags: string[];
  dateFrom: string;
  dateTo: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  view: 'list' | 'calendar';
  calendarView: 'day' | 'week' | 'month';
}

export interface MeetingFormData extends CreateMeetingData {
  selectedProjectId?: string;
  externalUsers: Array<{
    email: string;
    name?: string;
    type?: string;
    status?: string;
    respondedAt?: Date;
  }>;
  tags: string[];
}

export interface MeetingsState {
  // Data
  meetings: Meeting[];
  currentMeeting: Meeting | null;
  calendarEvents: CalendarEvent[];
  categories: any[];
  tags: string[];

  // Pagination & Filters
  filters: MeetingFilters;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };

  // Loading states
  loading: {
    meetings: boolean;
    currentMeeting: boolean;
    creating: boolean;
    updating: boolean;
    deleting: boolean;
    categories: boolean;
    calendarEvents: boolean;
  };

  // Error states
  errors: {
    meetings: string | null;
    currentMeeting: string | null;
    creating: string | null;
    updating: string | null;
    deleting: string | null;
    categories: string | null;
    calendarEvents: string | null;
  };

  // UI states
  ui: {
    drawerOpen: boolean;
    drawerMode: 'create' | 'edit' | 'view';
    selectedMeetingId: string | null;
    contextProjectId: string | null;
  };
}

const initialFilters: MeetingFilters = {
  search: '',
  status: 'all',
  visibility: 'all',
  categoryId: '',
  tags: [],
  dateFrom: '',
  dateTo: '',
  sortBy: 'startDateTime',
  sortOrder: 'asc',
  view: 'calendar',
  calendarView: 'month',
};

const initialState: MeetingsState = {
  meetings: [],
  currentMeeting: null,
  calendarEvents: [],
  categories: [],
  tags: [],

  filters: initialFilters,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  },

  loading: {
    meetings: false,
    currentMeeting: false,
    creating: false,
    updating: false,
    deleting: false,
    categories: false,
    calendarEvents: false,
  },

  errors: {
    meetings: null,
    currentMeeting: null,
    creating: null,
    updating: null,
    deleting: null,
    categories: null,
    calendarEvents: null,
  },

  ui: {
    drawerOpen: false,
    drawerMode: 'create',
    selectedMeetingId: null,
    contextProjectId: null,
  },
};

// Async thunks
export const fetchMeetings = createAsyncThunk(
  'meetings/fetchMeetings',
  async (params: { projectId?: string; filters?: Partial<MeetingListParams> }) => {
    const response = await meetingService.getMeetings(params.projectId, params.filters);

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch meetings');
    }

    return {
      data: response.data,
      projectId: params.projectId,
    };
  }
);

export const fetchMeeting = createAsyncThunk(
  'meetings/fetchMeeting',
  async (params: { projectId?: string; meetingId: string }) => {
    const response = await meetingService.getMeeting(params.projectId, params.meetingId);

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch meeting');
    }

    return response.data;
  }
);

export const createMeeting = createAsyncThunk(
  'meetings/createMeeting',
  async (params: { data: CreateMeetingData; projectId?: string }) => {
    const response = await meetingService.createMeeting(params.data, params.projectId);

    if (!response.success) {
      throw new Error(response.message || 'Failed to create meeting');
    }

    return response.data;
  }
);

export const updateMeeting = createAsyncThunk(
  'meetings/updateMeeting',
  async (params: {
    projectId?: string;
    meetingId: string;
    data: UpdateMeetingData;
    editType?: 'this_only' | 'this_and_future' | 'all_series';
  }) => {
    const response = await meetingService.updateMeeting(
      params.projectId,
      params.meetingId,
      params.data,
      params.editType
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to update meeting');
    }

    return response.data;
  }
);

export const deleteMeeting = createAsyncThunk(
  'meetings/deleteMeeting',
  async (params: {
    projectId?: string;
    meetingId: string;
    editType?: 'this_only' | 'this_and_future' | 'all_series';
  }) => {
    const response = await meetingService.deleteMeeting(
      params.projectId,
      params.meetingId,
      params.editType
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to delete meeting');
    }

    return params.meetingId;
  }
);

export const fetchCalendarEvents = createAsyncThunk(
  'meetings/fetchCalendarEvents',
  async (params: {
    projectId?: string;
    dateFrom?: string;
    dateTo?: string;
    calendarView?: 'day' | 'week' | 'month';
  }) => {
    const response = await meetingService.getCalendarEvents(params.projectId, {
      dateFrom: params.dateFrom,
      dateTo: params.dateTo,
      calendarView: params.calendarView,
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch calendar events');
    }

    return response.data || [];
  }
);

export const fetchMeetingCategories = createAsyncThunk(
  'meetings/fetchMeetingCategories',
  async () => {
    const response = await meetingService.getMeetingCategories();

    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch meeting categories');
    }

    return response.data || [];
  }
);

// Slice
const meetingsSlice = createSlice({
  name: 'meetings',
  initialState,
  reducers: {
    // Filter actions
    setFilters: (state, action: PayloadAction<Partial<MeetingFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
      // Reset pagination when filters change
      state.pagination.page = 1;
    },

    clearFilters: state => {
      state.filters = initialFilters;
      state.pagination.page = 1;
    },

    setSearch: (state, action: PayloadAction<string>) => {
      state.filters.search = action.payload;
      state.pagination.page = 1;
    },

    setView: (state, action: PayloadAction<'list' | 'calendar'>) => {
      state.filters.view = action.payload;
    },

    setCalendarView: (state, action: PayloadAction<'day' | 'week' | 'month'>) => {
      state.filters.calendarView = action.payload;
    },

    // Pagination actions
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },

    setPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.limit = action.payload;
      state.pagination.page = 1;
    },

    // UI actions
    openDrawer: (
      state,
      action: PayloadAction<{
        mode: 'create' | 'edit' | 'view';
        meetingId?: string;
        projectId?: string;
      }>
    ) => {
      state.ui.drawerOpen = true;
      state.ui.drawerMode = action.payload.mode;
      state.ui.selectedMeetingId = action.payload.meetingId || null;
      state.ui.contextProjectId = action.payload.projectId || null;
    },

    closeDrawer: state => {
      state.ui.drawerOpen = false;
      state.ui.selectedMeetingId = null;
      state.currentMeeting = null;
      state.errors.creating = null;
      state.errors.updating = null;
    },

    // Data actions
    addMeetingToList: (state, action: PayloadAction<Meeting>) => {
      state.meetings.unshift(action.payload);
      state.pagination.total += 1;
    },

    updateMeetingInList: (state, action: PayloadAction<Meeting>) => {
      const index = state.meetings.findIndex(
        meeting => (meeting._id || meeting.id) === (action.payload._id || action.payload.id)
      );
      if (index !== -1) {
        state.meetings[index] = action.payload;
      }
    },

    removeMeetingFromList: (state, action: PayloadAction<string>) => {
      state.meetings = state.meetings.filter(
        meeting => (meeting._id || meeting.id) !== action.payload
      );
      state.pagination.total = Math.max(0, state.pagination.total - 1);
    },

    // Error actions
    clearError: (state, action: PayloadAction<keyof MeetingsState['errors']>) => {
      state.errors[action.payload] = null;
    },

    clearAllErrors: state => {
      Object.keys(state.errors).forEach(key => {
        state.errors[key as keyof MeetingsState['errors']] = null;
      });
    },
  },

  extraReducers: builder => {
    // Fetch meetings
    builder
      .addCase(fetchMeetings.pending, state => {
        state.loading.meetings = true;
        state.errors.meetings = null;
      })
      .addCase(fetchMeetings.fulfilled, (state, action) => {
        state.loading.meetings = false;
        const { data } = action.payload;
        state.meetings = data?.meetings || [];
        state.pagination = {
          page: data?.pagination?.page || 1,
          limit: data?.pagination?.limit || 20,
          total: data?.pagination?.total || 0,
          totalPages: data?.pagination?.pages || 0,
        };
      })
      .addCase(fetchMeetings.rejected, (state, action) => {
        state.loading.meetings = false;
        state.errors.meetings = action.error.message || 'Failed to fetch meetings';
      });

    // Fetch single meeting
    builder
      .addCase(fetchMeeting.pending, state => {
        state.loading.currentMeeting = true;
        state.errors.currentMeeting = null;
      })
      .addCase(fetchMeeting.fulfilled, (state, action) => {
        state.loading.currentMeeting = false;
        state.currentMeeting = action.payload || null;
      })
      .addCase(fetchMeeting.rejected, (state, action) => {
        state.loading.currentMeeting = false;
        state.errors.currentMeeting = action.error.message || 'Failed to fetch meeting';
      });

    // Create meeting
    builder
      .addCase(createMeeting.pending, state => {
        state.loading.creating = true;
        state.errors.creating = null;
      })
      .addCase(createMeeting.fulfilled, (state, action) => {
        state.loading.creating = false;
        if (action.payload) {
          state.meetings.unshift(action.payload);
          state.pagination.total += 1;
        }
        state.ui.drawerOpen = false;
      })
      .addCase(createMeeting.rejected, (state, action) => {
        state.loading.creating = false;
        state.errors.creating = action.error.message || 'Failed to create meeting';
      });

    // Update meeting
    builder
      .addCase(updateMeeting.pending, state => {
        state.loading.updating = true;
        state.errors.updating = null;
      })
      .addCase(updateMeeting.fulfilled, (state, action) => {
        state.loading.updating = false;
        if (action.payload) {
          const index = state.meetings.findIndex(
            meeting => (meeting._id || meeting.id) === (action.payload!._id || action.payload!.id)
          );
          if (index !== -1) {
            state.meetings[index] = action.payload;
          }
          state.currentMeeting = action.payload;
        }
        state.ui.drawerOpen = false;
      })
      .addCase(updateMeeting.rejected, (state, action) => {
        state.loading.updating = false;
        state.errors.updating = action.error.message || 'Failed to update meeting';
      });

    // Delete meeting
    builder
      .addCase(deleteMeeting.pending, state => {
        state.loading.deleting = true;
        state.errors.deleting = null;
      })
      .addCase(deleteMeeting.fulfilled, (state, action) => {
        state.loading.deleting = false;
        state.meetings = state.meetings.filter(
          meeting => (meeting._id || meeting.id) !== action.payload
        );
        state.pagination.total = Math.max(0, state.pagination.total - 1);
        state.ui.drawerOpen = false;
        state.currentMeeting = null;
      })
      .addCase(deleteMeeting.rejected, (state, action) => {
        state.loading.deleting = false;
        state.errors.deleting = action.error.message || 'Failed to delete meeting';
      });

    // Fetch calendar events
    builder
      .addCase(fetchCalendarEvents.pending, state => {
        state.loading.calendarEvents = true;
        state.errors.calendarEvents = null;
      })
      .addCase(fetchCalendarEvents.fulfilled, (state, action) => {
        state.loading.calendarEvents = false;
        state.calendarEvents = action.payload || [];
      })
      .addCase(fetchCalendarEvents.rejected, (state, action) => {
        state.loading.calendarEvents = false;
        state.errors.calendarEvents = action.error.message || 'Failed to fetch calendar events';
      });

    // Fetch meeting categories
    builder
      .addCase(fetchMeetingCategories.pending, state => {
        state.loading.categories = true;
        state.errors.categories = null;
      })
      .addCase(fetchMeetingCategories.fulfilled, (state, action) => {
        state.loading.categories = false;
        state.categories = action.payload || [];
      })
      .addCase(fetchMeetingCategories.rejected, (state, action) => {
        state.loading.categories = false;
        state.errors.categories = action.error.message || 'Failed to fetch categories';
      });
  },
});

// Actions
export const {
  setFilters,
  clearFilters,
  setSearch,
  setView,
  setCalendarView,
  setPage,
  setPageSize,
  openDrawer,
  closeDrawer,
  addMeetingToList,
  updateMeetingInList,
  removeMeetingFromList,
  clearError,
  clearAllErrors,
} = meetingsSlice.actions;

// Selectors
export const selectMeetings = (state: { meetings: MeetingsState }) => state.meetings.meetings;
export const selectCurrentMeeting = (state: { meetings: MeetingsState }) =>
  state.meetings.currentMeeting;
export const selectCalendarEvents = (state: { meetings: MeetingsState }) =>
  state.meetings.calendarEvents;
export const selectMeetingFilters = (state: { meetings: MeetingsState }) => state.meetings.filters;
export const selectMeetingPagination = (state: { meetings: MeetingsState }) =>
  state.meetings.pagination;
export const selectMeetingLoading = (state: { meetings: MeetingsState }) => state.meetings.loading;
export const selectMeetingErrors = (state: { meetings: MeetingsState }) => state.meetings.errors;
export const selectMeetingUI = (state: { meetings: MeetingsState }) => state.meetings.ui;
export const selectMeetingCategories = (state: { meetings: MeetingsState }) =>
  state.meetings.categories;

export default meetingsSlice.reducer;
